import {computed, ref} from 'vue';
import {VidalInteraction} from '@/drug-interaction/interfaces';

export interface InteractionItem {
    id: number;
    code: string;
    name: string;
}

export type InteractionGroup = {
    item: InteractionItem;
    interactions: VidalInteraction[];
};

const maxSeverity = (g: InteractionGroup): number => {
    return Math.max(...g.interactions.map(i => severityLevel(i.severity)));
};

/**
 * Extract the severity level as a number from the severity string.
 */
export const severityLevel = (severity: string): number => {
    if (severity === 'INFO') {
        return -1;
    }

    return parseInt(severity.replace('LEVEL_', '')) || 0;
};

/**
 * Generate a unique key for an interaction, based on its id.
 */
export const interactionKey = (interaction: VidalInteraction) => interaction.id;

/**
 * Convert an HTML string to plain text, preserving paragraphs as newlines.
 */
export const toPlainText = (html: string) => {
    const spaced = html.replace(/<\/p>/gi, '</p>\n');
    return new DOMParser().parseFromString(spaced, 'text/html').body.innerText.trim();
};

export function useInteractionGroups(
    getInteractions: () => VidalInteraction[] | undefined,
    getItems: () => InteractionItem[]
) {
    const checkedKeys = ref(new Set<string>());
    const comments = ref<Record<number, string>>({});

    const toggleCheck = (interaction: VidalInteraction) => {
        const key = interactionKey(interaction);
        const next = new Set(checkedKeys.value);

        if (next.has(key)) {
            next.delete(key);
        } else {
            next.add(key);
        }

        checkedKeys.value = next;
    };

    /**
     * Group interactions by product, sorted by descending severity.
     * Groups themselves are sorted by their highest severity first.
     */
    const groupedByItem = computed((): InteractionGroup[] => {
        const interactions = getInteractions();

        if (!interactions) {
            return [];
        }

        const groups = interactions
            .flatMap(interaction =>
                interaction.vidal_drug_ids.map(({code}) => ({interaction, code: String(code)}))
            )
            .reduce<Record<number, { item: InteractionItem; interactions: VidalInteraction[] }>>(
                (acc, {interaction, code}) => {
                    const item = getItems().find(i => i.code === code);
                    if (!item) {
                        return acc;
                    }

                    if (!acc[item.id]) {
                        acc[item.id] = {item, interactions: []};
                    }

                    acc[item.id].interactions.push(interaction);

                    return acc;
                },
                {}
            );

        return Object.values(groups)
            .map(({item, interactions}) => ({
                item,
                interactions: interactions.slice().sort((a, b) => severityLevel(b.severity) - severityLevel(a.severity)),
            }))
            .sort((a, b) => {
                return maxSeverity(b) - maxSeverity(a);
            });
    });

    /**
     * Find the id of the item related to the given interaction list, excluding the current item.
     */
    const findRelatedItemId = (interactionList: VidalInteraction[], currentItemId: number): number | null => {
        const codes = interactionList.flatMap(i => i.vidal_drug_ids.map(({code}) => String(code)));
        return getItems().find(item => codes.includes(item.code) && item.id !== currentItemId)?.id ?? null;
    };

    return {checkedKeys, comments, toggleCheck, groupedByItem, findRelatedItemId};
}
